import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useState, useEffect, useMemo } from 'react';
import { Stack, Tabs, Tab, Typography, Box, Grid, CircularProgress } from '@mui/material';
import { PieChartWrapper, TreeChartWrapper, pieArcLabelClasses } from '../../charts';
import { useQuery } from 'react-query';
import {
  CensusCodeAReadDTO,
  CensusCodeBReadDTO,
  CensusCodeCReadDTO,
  DiagnosisEnergyVectorsReadDTO,
  DiagnosisEnergyUseReadDTO,
} from '../../../generated/api-client';
import { apiClient } from '../../../services/apiClient';
import { DiagnosisStepHandle } from './Step.types';
import {
  useDiagnosisEnergyVectorsByDiagnosisId,
  useDiagnosisEnergyUseByDiagnosisId,
  useVectorTypeOptions,
} from '../../../hooks';
import { getEnergyVectorOptionLabel } from '../../common/EnergyVectorDropdown';
import {
  FUNCTIONAL_AREA_VALUES,
  FunctionalAreaValue,
  getFunctionalAreaLabel,
} from '../../../utils/functionalArea';
import { functionalAreaColors } from '../../../utils/functionalAreaColors';
import { formatUnitOfMeasurement } from '../../../utils/unitOfMeasurementFormatter';
import { formatNumberItalian } from '../../../utils/numberFormat';

interface DiagnosisGerarchieStepProps {
  diagnosisId?: string;
}

type CensusData = CensusCodeAReadDTO | CensusCodeBReadDTO | CensusCodeCReadDTO;
type CensusType = 'A' | 'B' | 'C';

const getCensusAnnualConsumption = (
  census: CensusData,
  censusType: CensusType | null
): number => {
  if (censusType === 'B') {
    return Number((census as CensusCodeBReadDTO).annual_consumption_sm3a) || 0;
  }
  if (censusType === 'C') {
    return Number((census as CensusCodeCReadDTO).annual_consumption_liters) || 0;
  }
  return Number(census.annual_consumption_kwha) || 0;
};

interface ChartDatum {
  id: string;
  label: string;
  value: number;
  percentage: number;
  color: string;
}

interface TreeChartNodeData {
  id: string;
  name: string;
  value: number;
  unit: string;
  percent: number;
  color?: string;
  children?: TreeChartNodeData[];
}

// Convert hex color to rgba with opacity
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Generate color shades from base color (darker to lighter)
// Returns an array of hex colors from darkest (index 0) to lightest (last index)
const generateColorShades = (baseColor: string, count: number): string[] => {
  if (count === 0) return [];
  if (count === 1) return [baseColor];

  // Parse hex color
  const r = parseInt(baseColor.slice(1, 3), 16);
  const g = parseInt(baseColor.slice(3, 5), 16);
  const b = parseInt(baseColor.slice(5, 7), 16);

  const shades: string[] = [];

  // Generate shades from darker to lighter
  // Avoid pure black (0,0,0) and pure white (255,255,255)
  // Darkest: reduce by 30% (but not below 30)
  // Lightest: increase by 30% (but not above 240)
  for (let i = 0; i < count; i++) {
    const factor = i / (count - 1); // 0 to 1

    // Interpolate between darker and lighter
    // factor 0 = darker (reduce by 30%, min 30)
    // factor 1 = lighter (increase by 30%, max 240)
    const darkR = Math.max(30, Math.round(r * 0.7));
    const darkG = Math.max(30, Math.round(g * 0.7));
    const darkB = Math.max(30, Math.round(b * 0.7));

    const lightR = Math.min(240, Math.round(r + (255 - r) * 0.3));
    const lightG = Math.min(240, Math.round(g + (255 - g) * 0.3));
    const lightB = Math.min(240, Math.round(b + (255 - b) * 0.3));

    const shadeR = Math.round(darkR + (lightR - darkR) * factor);
    const shadeG = Math.round(darkG + (lightG - darkG) * factor);
    const shadeB = Math.round(darkB + (lightB - darkB) * factor);

    // Convert back to hex
    const hexR = shadeR.toString(16).padStart(2, '0');
    const hexG = shadeG.toString(16).padStart(2, '0');
    const hexB = shadeB.toString(16).padStart(2, '0');

    shades.push(`#${hexR}${hexG}${hexB}`);
  }

  return shades;
};

export const DiagnosisGerarchieStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisGerarchieStepProps>(({ diagnosisId }, ref) => {
    const [selectedVectorId, setSelectedVectorId] = useState<string | null>(null);

    const { data: diagnosisVectors = [], isLoading: vectorsLoading } =
      useDiagnosisEnergyVectorsByDiagnosisId(diagnosisId!);

    const { data: vectorTypeOptions = [] } = useVectorTypeOptions();

    const { data: energyUses = [], isLoading: energyUsesLoading } =
      useDiagnosisEnergyUseByDiagnosisId(diagnosisId);

    // Get selected vector
    const selectedVector = useMemo(() => {
      return diagnosisVectors.find(v => v.id === selectedVectorId);
    }, [diagnosisVectors, selectedVectorId]);

    // Determine census type based on vector type
    const censusType = useMemo(() => {
      if (!selectedVector?.vector_type) return null;
      const type = selectedVector.vector_type;
      if (type === 'Electricity') return 'A';
      if (type === 'NaturalGas') return 'B';
      if (type === 'Diesel') return 'C';
      return 'C';
    }, [selectedVector]);

    // Query census data
    const { data: censusDataA = [], isLoading: censusDataALoading } = useQuery(
      ['census-code-a', selectedVectorId],
      () =>
        apiClient.search_by_vector_id_census_code_a_search_by_vector_id__vector_id__get(
          selectedVectorId!
        ),
      { enabled: !!selectedVectorId && censusType === 'A' }
    );

    const { data: censusDataB = [], isLoading: censusDataBLoading } = useQuery(
      ['census-code-b', selectedVectorId],
      () =>
        apiClient.search_by_vector_id_census_code_b_search_by_vector_id__vector_id__get(
          selectedVectorId!
        ),
      { enabled: !!selectedVectorId && censusType === 'B' }
    );

    const { data: censusDataC = [], isLoading: censusDataCLoading } = useQuery(
      ['census-code-c', selectedVectorId],
      () =>
        apiClient.search_by_vector_id_census_code_c_search_by_vector_id__vector_id__get(
          selectedVectorId!
        ),
      { enabled: !!selectedVectorId && censusType !== 'A' && censusType !== 'B' }
    );

    // Determine if census data is loading
    const censusDataLoading = useMemo(() => {
      if (censusType === 'A') return censusDataALoading;
      if (censusType === 'B') return censusDataBLoading;
      if (censusType === 'C') return censusDataCLoading;
      return censusDataCLoading;
    }, [censusType, censusDataALoading, censusDataBLoading, censusDataCLoading]);

    // Overall loading state
    const isLoading = vectorsLoading || energyUsesLoading || censusDataLoading;

    // Get current census data based on type
    const currentCensusData = useMemo(() => {
      if (censusType === 'A') return censusDataA;
      if (censusType === 'B') return censusDataB;
      if (censusType === 'C') return censusDataC;
      return [];
    }, [censusType, censusDataA, censusDataB, censusDataC]);

    // Process data for tree and chart
    const { treeData, functionalAreaData, energyUseData } = useMemo(() => {
      if (!currentCensusData.length || !selectedVector) {
        return { treeData: null, functionalAreaData: [], energyUseData: [] };
      }

      const unit = formatUnitOfMeasurement(selectedVector.unit_of_measurement || '');

      // Create a map of energy use ID to energy use data
      const energyUseMap = new Map<string, DiagnosisEnergyUseReadDTO>();
      energyUses.forEach(use => {
        if (use.id) {
          energyUseMap.set(use.id, use);
        }
      });

      // Group by functional area, then by energy use
      // Structure: area -> energy use -> censuses
      const areaMap = new Map<string, Map<string, { censuses: CensusData[]; total: number }>>();

      currentCensusData.forEach(census => {
        const area = (census.functional_area as any) || 'Unknown';
        const energyUseId = (census.energy_use_id as any) || 'Unknown';

        if (!areaMap.has(area)) {
          areaMap.set(area, new Map());
        }
        const areaEnergyUseMap = areaMap.get(area)!;

        if (!areaEnergyUseMap.has(energyUseId)) {
          areaEnergyUseMap.set(energyUseId, { censuses: [], total: 0 });
        }
        const energyUseData = areaEnergyUseMap.get(energyUseId)!;
        energyUseData.censuses.push(census);
        energyUseData.total += getCensusAnnualConsumption(census, censusType);
      });

      // Ordine fisso delle aree: Processo Produttivo, Servizi Ausiliari, Servizi Generali
      // (le eventuali aree non riconosciute, es. "Unknown", vanno in fondo)
      const areaOrder = (area: string) => {
        const index = FUNCTIONAL_AREA_VALUES.indexOf(area as FunctionalAreaValue);
        return index === -1 ? FUNCTIONAL_AREA_VALUES.length : index;
      };
      const sortedAreaEntries = Array.from(areaMap.entries()).sort(
        ([areaA], [areaB]) => areaOrder(areaA) - areaOrder(areaB)
      );

      // Calculate total consumption
      let totalConsumption = 0;
      areaMap.forEach(areaEnergyUseMap => {
        areaEnergyUseMap.forEach(({ total }) => {
          totalConsumption += total;
        });
      });

      // Build tree data: Total -> Functional Areas -> Energy Uses (leaves)
      const areaChildren: TreeChartNodeData[] = sortedAreaEntries.map(
        ([area, areaEnergyUseMap]) => {
          // Calculate area total
          let areaTotal = 0;
          areaEnergyUseMap.forEach(({ total }) => {
            areaTotal += total;
          });
          const areaPercentage = totalConsumption > 0 ? (areaTotal / totalConsumption) * 100 : 0;

          // Get base color for this functional area
          const areaBaseColor = functionalAreaColors[area] || '#cccccc';

          // Convert energy use map to array and sort by total (descending)
          // Put "Altro" at the end if it exists
          const energyUseEntries = Array.from(areaEnergyUseMap.entries());

          // Separate "Altro" from others
          const altroEntry = energyUseEntries.find(([energyUseId]) => {
            const energyUse = energyUseMap.get(energyUseId);
            const code = energyUse?.code?.toLowerCase() || '';
            return code === 'altro';
          });

          const otherEntries = energyUseEntries.filter(([energyUseId]) => {
            const energyUse = energyUseMap.get(energyUseId);
            const code = energyUse?.code?.toLowerCase() || '';
            return code !== 'altro';
          });

          // Sort by total (descending)
          otherEntries.sort((a, b) => b[1].total - a[1].total);

          // Combine: sorted others + "Altro" at the end (if exists)
          const sortedEntries = altroEntry ? [...otherEntries, altroEntry] : otherEntries;

          // Generate color shades for this area
          const colorShades = generateColorShades(areaBaseColor, sortedEntries.length);

          // Build energy use children (leaves) with shades
          const energyUseChildren: TreeChartNodeData[] = sortedEntries.map(
            ([energyUseId, { total: energyUseTotal }], index) => {
              const energyUse = energyUseMap.get(energyUseId);
              const energyUseLabel = energyUse
                ? `${energyUse.code || energyUse.description || energyUseId}`
                : energyUseId === 'Unknown'
                  ? 'Non assegnato'
                  : energyUseId;
              const energyUsePercentage =
                totalConsumption > 0 ? (energyUseTotal / totalConsumption) * 100 : 0;

              // Use shade: first (largest) gets darkest, last gets lightest
              const shadeColor = colorShades[index] || areaBaseColor;

              return {
                id: `energy-use-${energyUseId}-${area}`,
                name: energyUseLabel,
                value: energyUseTotal,
                unit,
                percent: energyUsePercentage,
                color: hexToRgba(shadeColor, 0.7),
              };
            }
          );

          return {
            id: `area-${area}`,
            name: getFunctionalAreaLabel(area),
            value: areaTotal,
            unit,
            percent: areaPercentage,
            color: functionalAreaColors[area] || '#cccccc',
            children: energyUseChildren,
          };
        }
      );

      const rootTreeData: TreeChartNodeData = {
        id: 'total',
        name: 'Totale sito',
        value: totalConsumption,
        unit,
        percent: 100,
        color: '#bfbfbf',
        children: areaChildren,
      };

      // Build chart data for functional areas (inner ring)
      const functionalAreaChartData: ChartDatum[] = sortedAreaEntries.map(
        ([area, areaEnergyUseMap]) => {
          let areaTotal = 0;
          areaEnergyUseMap.forEach(({ total }) => {
            areaTotal += total;
          });
          const percentage = totalConsumption > 0 ? (areaTotal / totalConsumption) * 100 : 0;
          return {
            id: area,
            label: getFunctionalAreaLabel(area),
            value: areaTotal,
            percentage,
            color: functionalAreaColors[area] || '#cccccc',
          };
        }
      );

      // Build chart data for energy uses (outer ring)
      // Group by functional area first, then by energy use
      const energyUseChartData: ChartDatum[] = sortedAreaEntries.flatMap(
        ([area, areaEnergyUseMap]) => {
          let areaTotal = 0;
          areaEnergyUseMap.forEach(({ total }) => {
            areaTotal += total;
          });

          // Get base color for this functional area
          const areaBaseColor = functionalAreaColors[area] || '#cccccc';

          // Convert energy use map to array and sort by total (descending)
          // Put "Altro" at the end if it exists
          const energyUseEntries = Array.from(areaEnergyUseMap.entries());

          // Separate "Altro" from others
          const altroEntry = energyUseEntries.find(([energyUseId]) => {
            const energyUse = energyUseMap.get(energyUseId);
            const code = energyUse?.code?.toLowerCase() || '';
            return code === 'altro';
          });

          const otherEntries = energyUseEntries.filter(([energyUseId]) => {
            const energyUse = energyUseMap.get(energyUseId);
            const code = energyUse?.code?.toLowerCase() || '';
            return code !== 'altro';
          });

          // Sort by total (descending)
          otherEntries.sort((a, b) => b[1].total - a[1].total);

          // Combine: sorted others + "Altro" at the end (if exists)
          const sortedEntries = altroEntry ? [...otherEntries, altroEntry] : otherEntries;

          // Generate color shades for this area
          const colorShades = generateColorShades(areaBaseColor, sortedEntries.length);

          return sortedEntries.map(([energyUseId, { total }], index) => {
            const energyUse = energyUseMap.get(energyUseId);
            const energyUseLabel = energyUse
              ? `${energyUse.code || energyUse.description || energyUseId}`
              : energyUseId === 'Unknown'
                ? 'Non assegnato'
                : energyUseId;
            const percentage = areaTotal > 0 ? (total / areaTotal) * 100 : 0;

            // Use shade: first (largest) gets darkest, last gets lightest
            const shadeColor = colorShades[index] || areaBaseColor;

            return {
              id: `energy-use-${energyUseId}-${area}`,
              label: energyUseLabel,
              value: total,
              percentage,
              color: hexToRgba(shadeColor, 0.7),
            };
          });
        }
      );

      return {
        treeData: rootTreeData,
        functionalAreaData: functionalAreaChartData,
        energyUseData: energyUseChartData,
      };
    }, [currentCensusData, selectedVector, energyUses, censusType]);

    useEffect(() => {
      if (!diagnosisId) {
        setSelectedVectorId(null);
        return;
      }
      if (!diagnosisVectors.length) {
        setSelectedVectorId(null);
        return;
      }
      setSelectedVectorId(prev => {
        if (prev && diagnosisVectors.some(vector => vector.id === prev)) {
          return prev;
        }
        return diagnosisVectors[0]?.id ?? null;
      });
    }, [diagnosisVectors, diagnosisId]);

    React.useImperativeHandle(ref, () => ({
      save: async () => {
        return { success: true };
      },
    }));

    // Stesse dimensioni del grafico Monitor ENEA, aumentate del 50%
    const innerRadius = 60; // Buco al centro (40 * 1.5)
    const middleRadius = 180; // Fine primo strato (120 * 1.5)
    const outerRadius = 225; // Fine secondo strato (150 * 1.5)

    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing="1rem"
          alignItems="center"
          flexWrap="wrap"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing="1rem" alignItems="center" flexWrap="wrap">
            <Typography variant="h6">Gerarchia</Typography>
            {diagnosisVectors.length > 0 && (
              <Tabs
                value={selectedVectorId || false}
                onChange={(_, newValue) => setSelectedVectorId(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: 40 }}
              >
                {diagnosisVectors.map(vector => (
                  <Tab
                    key={vector.id}
                    label={getEnergyVectorOptionLabel(vector, vectorTypeOptions)}
                    value={vector.id}
                    sx={{ textTransform: 'none' }}
                  />
                ))}
              </Tabs>
            )}
          </Stack>
        </Stack>

        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <CircularProgress size={80} />
          </Box>
        )}

        {!isLoading && treeData && (
          <Grid sx={{ display: 'flex' }}>
            <Box sx={{ width: '1000px' }}>
              <TreeChartWrapper data={treeData} width={1000} height={700} />
            </Box>
            <Box>
              <PieChartWrapper
                width={1500}
                height={1050}
                unitOfMeasurement={treeData.unit || ''}
                slotProps={{
                  legend: {
                    direction: 'column',
                    position: { vertical: 'middle', horizontal: 'right' },
                  },
                }}
                margin={{ top: 30, right: 750, left: 30, bottom: 30 }}
                series={[
                  {
                    innerRadius,
                    outerRadius: middleRadius,
                    data: functionalAreaData,
                    arcLabel: item => `${item.id} (${(item as any).percentage.toFixed(2)}%)`,
                    valueFormatter: ({ value }) =>
                      formatNumberItalian(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                    highlightScope: { fade: 'global', highlight: 'item' },
                    highlighted: { additionalRadius: 2 },
                    cornerRadius: 0,
                  },
                  {
                    innerRadius: middleRadius,
                    outerRadius: outerRadius,
                    data: energyUseData,
                    arcLabel: item => '', //`${item.label} (${(item as any).percentage.toFixed(2)}%)`,
                    valueFormatter: ({ value }) =>
                      formatNumberItalian(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                    arcLabelRadius: 160,
                    highlightScope: { fade: 'global', highlight: 'item' },
                    highlighted: { additionalRadius: 2 },
                    cornerRadius: 0,
                  },
                ]}
                sx={{
                  [`& .${pieArcLabelClasses.root}`]: {
                    fontSize: '12px',
                  },
                }}
              ></PieChartWrapper>
            </Box>
          </Grid>
        )}

        {!isLoading && !treeData && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            Seleziona un vettore energetico per visualizzare il riepilogo
          </Typography>
        )}
      </Stack>
    );
  })
);

DiagnosisGerarchieStep.displayName = 'DiagnosisGerarchieStep';
